import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Severity, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Dependency audit (part 57).
 *
 * The scan reads the manifests shipped in this repository — apps/web/package-lock.json and
 * services/core/go.sum — and matches them with the advisory corpus stored in the database.
 * It never calls a third-party service and never edits a manifest. A finding is one
 * package+advisory pair; rescanning updates the observation instead of creating a duplicate.
 *
 * Acknowledgement is a human "I have seen this" record. It does not install the fixed version,
 * close the finding, or change the package. That is why the action is labelled รับทราบ, not แก้ไข.
 */

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low'
type FindingStatus = 'open' | 'acknowledged' | 'resolved' | 'accepted_risk'

interface VulnScan {
  id: string
  status: string
  triggered_by?: string
  manifest_digest: string
  package_count: number
  module_count: number
  dependency_count: number
  advisory_count: number
  finding_count: number
  new_finding_count: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  error_message?: string
  started_at: string
  completed_at?: string
  created_at: string
}

interface VulnFinding {
  id: string
  scan_id: string
  advisory_id: string
  package_name: string
  ecosystem: 'npm' | 'go'
  installed_version: string
  fixed_version: string
  severity: SeverityLevel
  cvss_score: number
  cvss_vector: string
  title: string
  summary: string
  reference_url: string
  status: FindingStatus
  sla_due_at: string
  first_seen_at: string
  last_seen_at: string
  acknowledged_by?: string
  acknowledged_by_name?: string
  acknowledged_at?: string
  ack_note?: string
  created_at: string
}

const FINDINGS_KEY = ['vuln-findings']
const SCANS_KEY = ['vuln-scans']

const statusLabel: Record<FindingStatus, string> = {
  open: 'เปิดอยู่',
  acknowledged: 'รับทราบแล้ว',
  resolved: 'แก้ไขแล้ว',
  accepted_risk: 'ยอมรับความเสี่ยง',
}

const scanStatusLabel: Record<string, string> = {
  running: 'กำลังสแกน',
  succeeded: 'สำเร็จ',
  failed: 'ล้มเหลว',
}

const ecosystemLabel: Record<string, string> = { npm: 'npm', go: 'Go module' }

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function dueText(iso: string): { text: string; tone: string } {
  const diff = new Date(iso).getTime() - Date.now()
  const days = Math.ceil(diff / 86_400_000)
  if (Number.isNaN(days)) return { text: 'ไม่ทราบกำหนด', tone: 'dim' }
  if (days < 0) return { text: `เกินกำหนด ${Math.abs(days)} วัน`, tone: 'sev-critical' }
  if (days === 0) return { text: 'ครบกำหนดวันนี้', tone: 'sev-high' }
  if (days <= 7) return { text: `เหลือ ${days} วัน`, tone: 'sev-high' }
  return { text: `เหลือ ${days} วัน`, tone: 'dim' }
}

function shortDigest(digest: string): string {
  return digest ? digest.slice(0, 12) : '—'
}

export function Vulnerabilities() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canScan = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const canAcknowledge = canScan

  const [severity, setSeverity] = useState<SeverityLevel | 'all'>('all')
  const [status, setStatus] = useState<FindingStatus | 'all'>('all')
  const [ackTarget, setAckTarget] = useState<VulnFinding | null>(null)
  const [note, setNote] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const findings = useQuery({
    queryKey: FINDINGS_KEY,
    queryFn: () => api<VulnFinding[]>('/v1/vuln/findings?limit=500'),
    staleTime: 15_000,
  })
  const scans = useQuery({
    queryKey: SCANS_KEY,
    queryFn: () => api<VulnScan[]>('/v1/vuln/scans?limit=30'),
    staleTime: 15_000,
  })

  const runScan = useMutation({
    mutationFn: () => apiWrite<VulnScan>('/v1/vuln/scans', 'POST', {}),
    onSuccess: (scan) => {
      setNotice(
        `สแกนเสร็จ · พบ ${number(scan.finding_count)} รายการ (ใหม่ ${number(scan.new_finding_count)}) ` +
        `จาก ${number(scan.dependency_count)} dependency · วิกฤต ${number(scan.critical_count)} · สูง ${number(scan.high_count)}`,
      )
      void qc.invalidateQueries({ queryKey: FINDINGS_KEY })
      void qc.invalidateQueries({ queryKey: SCANS_KEY })
    },
  })

  const acknowledge = useMutation({
    mutationFn: (input: { id: string; note: string }) =>
      apiWrite<VulnFinding>(`/v1/vuln/findings/${input.id}/acknowledge`, 'POST', { note: input.note }),
    onSuccess: (row) => {
      setAckTarget(null)
      setNote('')
      setNotice(`บันทึกการรับทราบ ${row.advisory_id} ของ ${row.package_name} แล้ว — สถานะซอฟต์แวร์ไม่เปลี่ยน`)
      void qc.invalidateQueries({ queryKey: FINDINGS_KEY })
    },
  })

  const rows = findings.data ?? []
  const visible = useMemo(
    () => rows.filter((row) =>
      (severity === 'all' || row.severity === severity) &&
      (status === 'all' || row.status === status)),
    [rows, severity, status],
  )

  const stats = useMemo(() => {
    const active = rows.filter((row) => row.status === 'open' || row.status === 'acknowledged')
    return {
      open: active.length,
      critical: active.filter((row) => row.severity === 'critical').length,
      high: active.filter((row) => row.severity === 'high').length,
      dueSoon: active.filter((row) => {
        const days = (new Date(row.sla_due_at).getTime() - Date.now()) / 86_400_000
        return days <= 7
      }).length,
    }
  }, [rows])

  const lastScan = scans.data?.[0]
  const busy = runScan.isPending
  const loading = findings.isLoading || scans.isLoading
  const loadError = findings.error ?? scans.error
  const actionError = runScan.error ?? acknowledge.error

  const refresh = () => {
    void findings.refetch()
    void scans.refetch()
  }

  const head = (
    <PageHead
      title="ช่องโหว่ซอฟต์แวร์และ Dependency Audit"
      sub="ตรวจ package-lock.json และ go.sum ของระบบนี้ เทียบกับทะเบียนคำแนะนำที่เก็บในฐานข้อมูล — ระบุแพ็กเกจ เวอร์ชันที่ติดตั้ง เวอร์ชันที่แก้แล้ว และกำหนดเวลาตามระดับ CVSS"
      actions={
        <>
          <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={findings.isFetching || scans.isFetching}>
            {findings.isFetching || scans.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
          <button className="btn btn-sm" onClick={() => runScan.mutate()} disabled={!canScan || busy}>
            {busy ? 'กำลังสแกน…' : 'สแกนเดี๋ยวนี้'}
          </button>
        </>
      }
    />
  )

  if (loading) {
    return (
      <>
        {head}
        <div className="stack"><Skel h={110} /><Skel h={300} /></div>
      </>
    )
  }

  if (loadError) {
    return (
      <>
        {head}
        <Empty title="โหลดผลการสแกนไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบว่าเส้นทาง /v1/vuln ถูก mount แล้ว" />
      </>
    )
  }

  return (
    <>
      {head}

      {notice ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-emerald)' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="sev sev-low"><i className="sev-dot" />สำเร็จ</span>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
          </div>
        </div>
      ) : null}

      {actionError ? (
        <div className="form-error" style={{ marginBottom: 14 }}>
          {errorText(actionError, 'ดำเนินการไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="ช่องโหว่ที่ยังเปิด" value={stats.open} unit="รายการ" foot="นับสถานะเปิดอยู่และรับทราบแล้ว" />
        <Stat label="ระดับวิกฤต" value={stats.critical} unit="รายการ" foot="CVSS 9.0 ขึ้นไป · กำหนดแก้ไข 7 วัน" />
        <Stat label="ระดับสูง" value={stats.high} unit="รายการ" foot="CVSS 7.0–8.9 · กำหนดแก้ไข 30 วัน" />
        <Stat label="ใกล้ครบกำหนด" value={stats.dueSoon} unit="รายการ" foot="เหลือไม่เกิน 7 วันหรือเลยกำหนดแล้ว" />
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">รายการช่องโหว่</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              แสดง {number(visible.length)} จาก {number(rows.length)} รายการ · เรียงตามความรุนแรงและกำหนดแก้ไข
            </div>
          </div>
          <span className="chip" style={{ marginLeft: 'auto' }}>
            {lastScan ? `สแกนล่าสุด ${thaiDateTime(lastScan.created_at)}` : 'ยังไม่เคยสแกน'}
          </span>
        </div>

        <div className="row" style={{ gap: 14, marginTop: 16, flexWrap: 'wrap' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 190 }}>
            <label htmlFor="vuln-severity">ระดับความรุนแรง</label>
            <select
              id="vuln-severity"
              className="select"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as SeverityLevel | 'all')}
            >
              <option value="all">ทุกระดับ</option>
              <option value="critical">วิกฤต</option>
              <option value="high">สูง</option>
              <option value="medium">ปานกลาง</option>
              <option value="low">ต่ำ</option>
            </select>
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 190 }}>
            <label htmlFor="vuln-status">สถานะ</label>
            <select
              id="vuln-status"
              className="select"
              value={status}
              onChange={(e) => setStatus(e.target.value as FindingStatus | 'all')}
            >
              <option value="all">ทุกสถานะ</option>
              <option value="open">เปิดอยู่</option>
              <option value="acknowledged">รับทราบแล้ว</option>
              <option value="resolved">แก้ไขแล้ว</option>
              <option value="accepted_risk">ยอมรับความเสี่ยง</option>
            </select>
          </div>
        </div>

        {visible.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <Empty
              title={rows.length === 0 ? 'ยังไม่มีช่องโหว่ที่บันทึกไว้' : 'ไม่พบรายการตามตัวกรอง'}
              body={rows.length === 0
                ? 'กด “สแกนเดี๋ยวนี้” เพื่ออ่าน package-lock.json และ go.sum แล้วเทียบกับทะเบียนคำแนะนำ'
                : 'ลองเปลี่ยนระดับความรุนแรงหรือสถานะที่กรอง'}
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>ระดับ</th>
                  <th>แพ็กเกจและเวอร์ชัน</th>
                  <th>คำแนะนำ</th>
                  <th style={{ width: 80 }}>CVSS</th>
                  <th style={{ width: 150 }}>กำหนดแก้ไข</th>
                  <th style={{ width: 120 }}>สถานะ</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const due = dueText(row.sla_due_at)
                  const pending = acknowledge.isPending && acknowledge.variables?.id === row.id
                  const isAcked = row.status === 'acknowledged'
                  return (
                    <tr key={row.id} style={{ cursor: 'default' }}>
                      <td><Severity value={row.severity} /></td>
                      <td>
                        <div className="mono" style={{ color: 'var(--color-ivory)', fontSize: 12 }}>
                          {row.package_name}
                        </div>
                        <div className="dim tnum" style={{ fontSize: 11, marginTop: 4, whiteSpace: 'nowrap' }}>
                          {row.installed_version}
                          <span style={{ margin: '0 6px', color: 'var(--color-ivory-dim)' }}>→</span>
                          {row.fixed_version || 'ยังไม่มีเวอร์ชันที่แก้'}
                          <span className="chip" style={{ marginLeft: 8, fontSize: 10 }}>
                            {ecosystemLabel[row.ecosystem] ?? row.ecosystem}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 12, lineHeight: 1.5 }}>{row.title}</div>
                        <div className="mono dim" style={{ fontSize: 10.5, marginTop: 4 }}>{row.advisory_id}</div>
                      </td>
                      <td className="tnum" style={{ whiteSpace: 'nowrap' }} title={row.cvss_vector}>
                        {row.cvss_score.toFixed(1)}
                      </td>
                      <td>
                        <div className={due.tone} style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{due.text}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3, whiteSpace: 'nowrap' }}>
                          {thaiDateTime(row.sla_due_at)}
                        </div>
                      </td>
                      <td>
                        <span className={`chip${isAcked ? ' gold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                          {statusLabel[row.status] ?? row.status}
                        </span>
                        {isAcked && row.acknowledged_at ? (
                          <div className="dim" style={{ fontSize: 10, marginTop: 4 }}>
                            {row.acknowledged_by_name || 'ผู้ตรวจทาน'} · {thaiDateTime(row.acknowledged_at)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {canAcknowledge && (row.status === 'open' || row.status === 'acknowledged') ? (
                          <button
                            className="btn btn-sm btn-ghost"
                            type="button"
                            disabled={pending}
                            onClick={() => {
                              setNotice(null)
                              setAckTarget(row)
                              setNote(row.ack_note ?? '')
                              acknowledge.reset()
                            }}
                          >
                            {isAcked ? 'แก้บันทึก' : 'รับทราบ'}
                          </button>
                        ) : (
                          <span className="dim" style={{ fontSize: 11 }}>—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {ackTarget ? (
          <div className="card card-pad" style={{ marginTop: 16, borderColor: 'var(--color-gold-dim)' }}>
            <div className="card-label">บันทึกการรับทราบ</div>
            <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 0' }}>
              {ackTarget.advisory_id} · {ackTarget.package_name} {ackTarget.installed_version}
              {' — '}การรับทราบยืนยันว่ามีผู้เห็นรายการนี้แล้ว ไม่ได้ติดตั้งเวอร์ชันที่แก้ให้
            </p>
            <div className="field">
              <label htmlFor="vuln-ack-note">บันทึกของผู้ตรวจทาน (ไม่บังคับ)</label>
              <textarea
                id="vuln-ack-note"
                className="input"
                style={{ height: 76, padding: '10px 12px', resize: 'vertical' }}
                value={note}
                maxLength={2000}
                onChange={(e) => setNote(e.target.value)}
                placeholder="เช่น อยู่ในแผนอัปเกรดรอบถัดไป หรือมีมาตรการชดเชยชั่วคราว"
              />
            </div>
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-sm btn-ghost" onClick={() => { setAckTarget(null); setNote('') }} disabled={acknowledge.isPending}>
                ยกเลิก
              </button>
              <button
                className="btn btn-sm"
                disabled={acknowledge.isPending}
                onClick={() => acknowledge.mutate({ id: ackTarget.id, note: note.trim() })}
              >
                {acknowledge.isPending ? 'กำลังบันทึก…' : 'ยืนยันรับทราบ'}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-label">ประวัติการสแกน</div>
          <span className="chip" style={{ marginLeft: 'auto' }}>
            ทะเบียนคำแนะนำ {number(lastScan?.advisory_count ?? 0)} รายการ
          </span>
        </div>

        {(scans.data ?? []).length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ยังไม่เคยสแกน" body="ผลการสแกนแต่ละครั้งจะถูกเก็บไว้ พร้อมลายนิ้วมือของ manifest ที่อ่าน" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>ขอบเขตที่อ่าน</th>
                  <th>ผลที่พบ</th>
                  <th>ระดับ</th>
                  <th>Manifest digest</th>
                  <th style={{ width: 100 }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {(scans.data ?? []).map((scan) => (
                  <tr key={scan.id} style={{ cursor: 'default' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(scan.created_at)}</td>
                    <td className="dim" style={{ fontSize: 11.5 }}>
                      npm {number(scan.package_count)} · Go {number(scan.module_count)} · รวม {number(scan.dependency_count)}
                    </td>
                    <td>
                      <span className="tnum">{number(scan.finding_count)}</span>
                      <span className="dim"> พบ · ใหม่ </span>
                      <span className="tnum">{number(scan.new_finding_count)}</span>
                    </td>
                    <td className="dim tnum" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      วิกฤต {scan.critical_count} · สูง {scan.high_count} · กลาง {scan.medium_count} · ต่ำ {scan.low_count}
                    </td>
                    <td className="mono dim" style={{ fontSize: 10.5 }} title={scan.manifest_digest}>
                      {shortDigest(scan.manifest_digest)}
                    </td>
                    <td>
                      <span className={`chip${scan.status === 'succeeded' ? ' gold' : ''}`}>
                        {scanStatusLabel[scan.status] ?? scan.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">ขอบเขตของการสแกนนี้</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>อ่านไฟล์ <span className="mono">apps/web/package-lock.json</span> และ <span className="mono">services/core/go.sum</span> ในเครื่องที่รัน API เท่านั้น</li>
          <li>เทียบกับทะเบียนคำแนะนำในฐานข้อมูล ไม่เรียก NVD, OSV หรือบริการภายนอกระหว่างกดสแกน</li>
          <li>หนึ่งรายการคือหนึ่งคู่แพ็กเกจและคำแนะนำ — สแกนซ้ำจะอัปเดตเวอร์ชันที่พบและเวลาที่เห็นล่าสุด ไม่สร้างแถวซ้ำ</li>
          <li>ระดับความรุนแรงมาจากคะแนน CVSS ของคำแนะนำ และกำหนดแก้ไขคือ 7 / 30 / 90 / 180 วันตามระดับ</li>
          <li>การรับทราบไม่แก้ไข dependency และไม่ปิดช่องโหว่ — ต้องอัปเกรดเวอร์ชันหรือมีมาตรการชดเชยแยกต่างหาก</li>
        </ul>
      </div>
    </>
  )
}
